// Third-party library imports
import {
    createAction,
    createSelector,
    createSlice,
    PayloadAction
} from "@reduxjs/toolkit";
import {
    get,
    includes,
    isEqual,
    keyBy,
    merge,
    uniqWith,
    values
} from "lodash-es";

// Constants
import {
    DETAILS_TAB,
    TASKS_PAGE,
    TASK_HISTORY_PAGE
} from "../../config/tableAndPageConstants";
import {
    commonInvoiceListInvoicesSortOptions,
    invoicesStateFilterOptions
} from "../../constants/invoicesSortAndFilters";
import { paymentScheduleSortByOptions } from "../../constants/paymentPlansSortAndFilters";
import {
    taskHistoryDetailsDeliverySortByOptions,
    taskHistoryItemDetailsNotificationSortOptions,
    taskHistorySortByOptions,
    tasksSortByOptions,
    tasksStatusFilterOptions
} from "../../constants/tasksSortAndFilters";

// Types & interfaces
import { PageData } from "../common/types";
import {
    GetCommonInvoiceListInvoicesRequestPayload,
    Invoice
} from "../invoices/types";
import {
    GetPaymentPlanDataScheduleRequestPayload,
    PaymentPlanSchedule
} from "../paymentPlans/types";
import { User } from "../users/types";
import { GetRemittanceAdvicesRequestPayload } from "../remittanceAdvices/types";
import {
    GetActionedPhoneCallDataRequest,
    GetExtendedInvoicesRequestPayload,
    GetNotificationTasksRequestPayload,
    GetTaskHistoryActionedTasksRequestPayload,
    GetTaskHistoryChangedTasksRequestPayload,
    GetTaskHistoryDeliveryReportRequestPayload,
    GetTaskHistoryNotificationsRequestPayload,
    GetTaskHistoryRequestPayload,
    GetTaskHistoryTasksFailedRequestPayload,
    GetTasksRequestPayload,
    NotifyCustomersRequestPayload,
    SetSelectedTaskHistoryPayload,
    SetSelectedTaskHistoryRequestPayload,
    Task,
    TaskBatch,
    TaskDeliveryReport,
    TaskHistory,
    TaskHistoryNotification,
    BulkSaveTaskDetailsUpdateRequestPayload,
    TasksState,
    GetTaskHistoryCollectionTasksRequestPayload,
    GetTaskViewerDetailForUserRequestPayload,
    TaskWorkflowFilter,
    GetTaskPropertiesRequestPayload,
    GetWorkflowStepOptionsRequestPayload,
    TaskAddCommentRequestPayload,
    TaskActionPaymentVerificationTaskRequestPayload,
    TaskActionPaymentRALinkTaskRequestPayload,
    TaskActionOrganisationPaymentRALinkTaskRequestPayload,
    TaskActionRequestRATaskRequestPayload,
    TaskActionRequestOrganisationRATaskRequestPayload,
    ChangeTaskViewingRequestPayload,
    TaskActionPhoneCallTaskRequest,
    TasksFetchedForOrganisationPayload,
    GetTaskHistoryCancelPaymentPlanRequestPayload,
    ActionTaskOptionsRequestPayload
} from "./types";

// Utility functions
import {
    DynamicObject,
    ResponseModalObject
} from "../../utils/commonInterfaces";

// Redux actions
import type { RootState } from "../storeTypes";

const actionPrefix = 'task';

// Type-safe initialState!
export const initialState: TasksState = {
    activeTasks: {
        loading: false,
        saveTaskDataLoading: false,
        errorMessages: [],
        data: [],
        pageData: {
            pageSize: TASKS_PAGE.pageSize,
            currentPage: 0,
            hasNextPage: false,
        },
        filters: {},
        taskStatus: get(tasksStatusFilterOptions ?? [], '2.value', 'Available').toString(),
        taskTableFilter: undefined,
        sortBy: tasksSortByOptions[1].value,
        actionFilterOptions: {},
        actionFilterOptionsLoading: false,
        sortAscending: true,
        viewingUser: {
            user: undefined,
            loading: false
        },
        activeData: {
            record: {},
            loading: false,
            selectedId: null,
            companyId: null,
            errorMessages: [],
            invoices: {
                loading: false,
                errorMessages: [],
                data: [],
                pageData: {
                    pageSize: DETAILS_TAB.INVOICE_LIST.pageSize,
                    currentPage: 0,
                    hasNextPage: false,
                },
                filters: {},
                sortBy: commonInvoiceListInvoicesSortOptions[0].value, //tasksSortByOptions[0].value,
                sortAscending: true,
                invoiceState: get(
                    invoicesStateFilterOptions,
                    `${1}.value`
                ),
            },
            schedule: {
                loading: false,
                errorMessages: [],
                data: [],
                pageData: {
                    pageSize: DETAILS_TAB.PAYMENT_PLAN_SCHEDULE_LIST.pageSize,
                    currentPage: 0,
                    hasNextPage: false,
                },
                filters: {},
                sortBy: paymentScheduleSortByOptions[0].value, // 2 for Invoice number
                sortAscending: true,
            }
        },
        notifyCustomersLoading: false,
        compactView: false,
        compactViewLoading: false,
        toggledExpandStatusIds: [],
    },
    taskHistory: {
        loading: false,
        errorMessages: [],
        data: [],
        pageData: {
            pageSize: TASK_HISTORY_PAGE.pageSize,
            currentPage: 0,
            hasNextPage: false,
        },
        filters: {},
        taskTableFilter: undefined,
        sortBy: taskHistorySortByOptions[1].value,
        sortAscending: false, // default is descending
        activeData: {
            record: {},
            loading: false,
            selectedId: null,
            companyId: null,
            errorMessages: [],
            notifications: {
                loading: false,
                errorMessages: [],
                data: [],
                pageData: {
                    pageSize: TASK_HISTORY_PAGE.notifications.pageSize,
                    currentPage: 0,
                    hasNextPage: false,
                },
                filters: {},
                sortBy: taskHistoryItemDetailsNotificationSortOptions[0].value,
                sortAscending: true,
                activeData: {
                    loading: false,
                    selectedId: null,
                    errorMessages: [],
                    record: {},
                    invoices: {
                        loading: false,
                        errorMessages: [],
                        data: [],
                        pageData: {
                            pageSize: DETAILS_TAB.INVOICE_LIST.pageSize,
                            currentPage: 0,
                            hasNextPage: false,
                        },
                        filters: {},
                        sortBy: commonInvoiceListInvoicesSortOptions[0].value, //tasksSortByOptions[0].value,
                        sortAscending: true,
                        invoiceState: get(
                            invoicesStateFilterOptions,
                            `${1}.value`
                        ),
                    },
                    tasks: {
                        loading: false,
                        errorMessages: [],
                        data: [],
                        pageData: {
                            pageSize:
                                TASK_HISTORY_PAGE.notifications.tasks.pageSize,
                            currentPage: 0,
                            hasNextPage: false,
                        },
                        filters: {},
                        sortBy: '', //tasksSortByOptions[0].value,
                        sortAscending: true,
                    },
                },
            },
            changedTasks: {
                activeData: {
                    loading: false,
                    record: {},
                    errorMessages: [],
                },
                list: {
                    loading: false,
                    errorMessages: [],
                    data: [],
                    pageData: {
                        pageSize: TASK_HISTORY_PAGE.changedTasks.pageSize,
                        currentPage: 0,
                        hasNextPage: false,
                    },
                },
            },
            collections: {
                activeData: {
                    loading: false,
                    record: {},
                    errorMessages: [],
                },
                actionedTasks: {
                    loading: false,
                    errorMessages: [],
                    data: [],
                    pageData: {
                        pageSize: TASK_HISTORY_PAGE.collections.pageSize,
                        currentPage: 0,
                        hasNextPage: false,
                    },
                    activeData: {
                        loading: false,
                        selectedId: null,
                        errorMessages: [],
                        record: {},
                    },
                },
            },
            failedList: {
                loading: false,
                errorMessages: [],
                data: [],
                pageData: {
                    pageSize: TASK_HISTORY_PAGE.failedTasks.pageSize,
                    currentPage: 0,
                    hasNextPage: false,
                },
            },
            detailsDelivery: {
                loading: false,
                pageData: {
                    pageSize: TASK_HISTORY_PAGE.deliveryReport.tasks.pageSize,
                    currentPage: 0,
                    hasNextPage: false,
                },
                filters: {},
                sortAscending: true,
                sortBy: taskHistoryDetailsDeliverySortByOptions[1].value,
                data: [],
                activeData: {
                    loading: false,
                    selectedId: null,
                    errorMessages: [],
                    record: {},
                },
            },
            cancelledPaymentPlanTasks: {
                activeData: {
                    loading: false,
                    record: {},
                    errorMessages: [],
                },
                list: {
                    loading: false,
                    errorMessages: [],
                    data: [],
                    pageData: {
                        pageSize: TASK_HISTORY_PAGE.changedTasks.pageSize,
                        currentPage: 0,
                        hasNextPage: false,
                    },
                },
            }
        },
    },
    taskWorkflows: {
        data: [],
        errorMessages: [],
        loading: false
    },
};

const taskSlice = createSlice({
    name: actionPrefix,
    initialState,
    reducers: {
        getTasksForOrganisationRequestAction: (
            state,
            _action: PayloadAction<GetTasksRequestPayload>
        ) => {
            state.activeTasks.loading = true;
        },
        getTasksRequestAction: (
            state,
            _action: PayloadAction<GetTasksRequestPayload>
        ) => {
            state.activeTasks.loading = true;
        },
        getTasksSuccessAction: (
            state,
            action: PayloadAction<{
                data: Task[];
                pageData: PageData;
                mergeData?: boolean;
            }>
        ) => {
            if (action.payload.mergeData === true) {
                state.activeTasks.data = values(
                    merge(
                        keyBy(state.activeTasks.data, 'Id'),
                        keyBy(action.payload.data, 'Id')
                    )
                );
            } else if (action.payload.pageData.currentPage === 0) {
                state.activeTasks.data = action.payload.data;
            } else {
                state.activeTasks.data = uniqWith(
                    [...state.activeTasks.data, ...action.payload.data],
                    isEqual
                );
            }

            state.activeTasks.loading = false;
            state.activeTasks.pageData = action.payload.pageData;
            state.activeTasks.errorMessages = initialState.activeTasks.errorMessages;
        },
        getTasksErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeTasks.loading = false;
            state.activeTasks.data = initialState.activeTasks.data;
            state.activeTasks.errorMessages = action.payload;
        },
        updateTasksFiltersAction: (
            state,
            action: PayloadAction<GetTasksRequestPayload['filters']>
        ) => {
            state.activeTasks.filters = action.payload;
        },
        updateTasksSortByAndStatusAction: (
            state,
            action: PayloadAction<{
                sortBy: GetTasksRequestPayload['sortBy'];
                sortAscending: boolean;
                taskStatus: string;
            }>
        ) => {
            state.activeTasks.sortBy = action.payload.sortBy;
            state.activeTasks.sortAscending = action.payload.sortAscending;
            state.activeTasks.taskStatus = action.payload.taskStatus;
        },
        updateTasksTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeTasks.taskTableFilter = action.payload;
        },
        clearTasksStateAllTableFiltersAction: (state) => {
            state.activeTasks.filters = initialState.activeTasks.filters;
            state.activeTasks.sortBy = initialState.activeTasks.sortBy;
            state.activeTasks.sortAscending = initialState.activeTasks.sortAscending;
            state.activeTasks.taskStatus = initialState.activeTasks.taskStatus;
            state.activeTasks.taskTableFilter = initialState.activeTasks.taskTableFilter;
        },
        clearTasksStateDataAction: (state) => {
            state.activeTasks = initialState.activeTasks;
        },
        getTasksActionFilterOptionsRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    companyIds: string[] | undefined;
                    isOrgView: boolean;
                }>
            ) => {
                state.activeTasks.actionFilterOptionsLoading = true;
            },
            prepare: (
                companyIds: string[] | undefined = undefined,
                isOrgView: boolean = false
            ) => ({
                payload: {
                    companyIds,
                    isOrgView
                }
            })
        },
        getTasksActionFilterOptionsResponseAction: (
            state,
            action: PayloadAction<DynamicObject>
        ) => {
            state.activeTasks.actionFilterOptionsLoading = false;
            state.activeTasks.actionFilterOptions = action.payload;
        },
        getTableCompactViewRequestAction: (
            state,
            _action: PayloadAction<boolean | undefined>
        ) => {
            state.activeTasks.compactViewLoading = true;
        },
        getTableCompactViewSuccessAction: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.activeTasks.compactViewLoading = false;
            state.activeTasks.compactView = action.payload;
        },
        setTableCompactViewRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    isCompact: boolean;
                    callback?: (res: ResponseModalObject) => void;
                }>
            ) => {
                state.activeTasks.compactViewLoading = true;
            },
            prepare: (
                isCompact: boolean,
                callback?: (res: ResponseModalObject) => void
            ) => ({
                payload: {
                    isCompact,
                    callback
                }
            })
        },
        setTableCompactViewSuccessAction: (
            state,
            _action: PayloadAction<boolean | undefined>
        ) => {
            state.activeTasks.compactViewLoading = false;
        },
        setTaskExpandStatusAction: (
            state,
            action: PayloadAction<string | undefined>
        ) => {
            const taskId = action.payload;

            if (!taskId) {
                return; // Exit early if taskId is null or undefined
            }

            if (!includes(state.activeTasks.toggledExpandStatusIds, taskId)) {
                // Add task ID if it's not in the list
                state.activeTasks.toggledExpandStatusIds.push(taskId);
            } else {
                // Remove task ID if it's already in the list
                state.activeTasks.toggledExpandStatusIds = state.activeTasks.toggledExpandStatusIds.filter(
                    (id) => id !== taskId
                );
            }
        },
        setSelectedTaskIdRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    taskId: string | null;
                    callback: () => void;
                    companyId: string | null;
                }>
            ) => {
                state.activeTasks.activeData.selectedId = initialState.activeTasks.activeData.selectedId;
                state.activeTasks.activeData.companyId = initialState.activeTasks.activeData.companyId;
            },
            prepare: (
                taskId: string | null,
                callback: () => void,
                companyId: string | null = null,
            ) => ({
                payload: {
                    taskId,
                    callback,
                    companyId
                }
            })
        },
        setSelectedTaskIdSuccessAction: {
            reducer: (
                state,
                action: PayloadAction<{
                    taskId: string | null;
                    companyId: string | null;
                }>
            ) => {
                const { taskId, companyId } = action.payload;

                state.activeTasks.activeData.selectedId = taskId;
                state.activeTasks.activeData.companyId = companyId;
            },
            prepare: (
                taskId: string | null,
                companyId: string | null
            ) => ({
                payload: {
                    taskId,
                    companyId
                }
            })
        },
        getTaskDataForOrganisationRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    taskId: string;
                    companyId: string;
                    region: string;
                    lockedDeliveryMethod?: boolean;
                    isUsingCloudImportType?: boolean;
                    usingMultipleWorkflow?: boolean;
                    isPaymentPlanEnabled?: boolean;
                }>
            ) => {
                state.activeTasks.activeData.record = initialState.activeTasks.activeData.record;
                state.activeTasks.activeData.loading = true;
            },
            prepare: (
                taskId: string,
                companyId: string,
                region: string,
                lockedDeliveryMethod?: boolean,
                isUsingCloudImportType?: boolean,
                usingMultipleWorkflow?: boolean,
                isPaymentPlanEnabled?: boolean,
            ) => ({
                payload: {
                    taskId,
                    companyId,
                    region,
                    lockedDeliveryMethod,
                    isUsingCloudImportType,
                    usingMultipleWorkflow,
                    isPaymentPlanEnabled
                }
            })
        },
        getTaskDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    taskId: string;
                    companyId: string;
                    lockedDeliveryMethod?: boolean;
                    isUsingCloudImportType?: boolean;
                    usingMultipleWorkflow?: boolean;
                    isPaymentPlanEnabled?: boolean;
                    isCalendarView?: boolean;
                }>
            ) => {
                state.activeTasks.activeData.record = initialState.activeTasks.activeData.record;
                state.activeTasks.activeData.loading = true;
            },
            prepare: (
                taskId: string,
                companyId: string,
                lockedDeliveryMethod?: boolean,
                isUsingCloudImportType?: boolean,
                usingMultipleWorkflow?: boolean,
                isPaymentPlanEnabled?: boolean,
                isCalendarView?: boolean
            ) => ({
                payload: {
                    taskId,
                    companyId,
                    lockedDeliveryMethod,
                    isUsingCloudImportType,
                    usingMultipleWorkflow,
                    isPaymentPlanEnabled,
                    isCalendarView
                }
            })
        },
        getTaskDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.activeTasks.activeData.record = action.payload.record;
            state.activeTasks.activeData.loading = false;
            state.activeTasks.activeData.errorMessages = initialState.activeTasks.activeData.errorMessages;
        },
        getTaskDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeTasks.activeData.record = initialState.activeTasks.activeData.record;
            state.activeTasks.activeData.loading = false;
            state.activeTasks.activeData.errorMessages = action.payload;
        },
        clearTaskDataAction: (state) => {
            state.activeTasks.activeData = initialState.activeTasks.activeData;
        },
        notifyCustomersRequestAction: (
            state,
            _action: PayloadAction<NotifyCustomersRequestPayload>
        ) => {
            state.activeTasks.notifyCustomersLoading = true;
        },
        notifyOrganisationCustomersRequestAction: (
            state,
            _action: PayloadAction<NotifyCustomersRequestPayload>
        ) => {
            state.activeTasks.notifyCustomersLoading = true;
        },
        notifyCustomersResponseAction: (state) => {
            state.activeTasks.notifyCustomersLoading = false;
        },
        getTaskTicketInvoicesRequestAction: (
            state,
            _action: PayloadAction<GetCommonInvoiceListInvoicesRequestPayload>
        ) => {
            state.activeTasks.activeData.invoices.loading = true;
        },
        getTaskTicketInvoicesSuccessAction: (
            state,
            action: PayloadAction<{
                data: Invoice[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeTasks.activeData.invoices.data = action.payload.data;
            } else {
                state.activeTasks.activeData.invoices.data = uniqWith([
                    ...state.activeTasks.activeData.invoices.data,
                    ...action.payload.data],
                    isEqual
                );
            }

            state.activeTasks.activeData.invoices.loading = false;
            state.activeTasks.activeData.invoices.pageData = action.payload.pageData;
            state.activeTasks.activeData.invoices.errorMessages = initialState.activeTasks.activeData.invoices.errorMessages;
        },
        getTaskTicketInvoicesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeTasks.activeData.invoices.loading = false;
            state.activeTasks.activeData.invoices.data = initialState.activeTasks.activeData.invoices.data;
            state.activeTasks.activeData.invoices.errorMessages = action.payload;
        },
        updateTaskTicketInvoicesFiltersAction: (
            state,
            action: PayloadAction<GetCommonInvoiceListInvoicesRequestPayload['filters']>
        ) => {
            state.activeTasks.activeData.invoices.filters = action.payload;
        },
        updateTaskTicketInvoicesTableFiltersAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeTasks.activeData.invoices.invoiceState = action.payload;
        },
        updateTaskTicketInvoicesSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetCommonInvoiceListInvoicesRequestPayload['sortBy'];
                sortAscending: boolean;
                invoiceState: string;
            }>
        ) => {
            state.activeTasks.activeData.invoices.sortBy = action.payload.sortBy;
            state.activeTasks.activeData.invoices.sortAscending = action.payload.sortAscending;
            state.activeTasks.activeData.invoices.invoiceState = action.payload.invoiceState;
        },
        getTaskPaymentPlanScheduleRequestAction: (
            state,
            _action: PayloadAction<GetPaymentPlanDataScheduleRequestPayload>
        ) => {
            state.activeTasks.activeData.schedule.loading = true;
        },
        getTaskPaymentPlanScheduleSuccessAction: (
            state,
            action: PayloadAction<{
                data: PaymentPlanSchedule[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeTasks.activeData.schedule.data = action.payload.data;
            } else {
                state.activeTasks.activeData.schedule.data = uniqWith(
                    [
                        ...state.activeTasks.activeData.schedule.data,
                        ...action.payload.data
                    ],
                    isEqual
                );
            }

            state.activeTasks.activeData.schedule.loading = false;
            state.activeTasks.activeData.schedule.pageData = action.payload.pageData;
            state.activeTasks.activeData.schedule.errorMessages = initialState.activeTasks.activeData.schedule.errorMessages;
        },
        getTaskPaymentPlanScheduleErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeTasks.activeData.schedule.loading = false;
            state.activeTasks.activeData.schedule.data = initialState.activeTasks.activeData.schedule.data;
            state.activeTasks.activeData.schedule.errorMessages = action.payload;
        },
        updateTaskPaymentPlanScheduleFiltersAction: (
            state,
            action: PayloadAction<GetPaymentPlanDataScheduleRequestPayload['filters']>
        ) => {
            state.activeTasks.activeData.schedule.filters = action.payload;
        },
        updateTaskPaymentPlanScheduleSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetPaymentPlanDataScheduleRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeTasks.activeData.schedule.sortBy = action.payload.sortBy;
            state.activeTasks.activeData.schedule.sortAscending = action.payload.sortAscending;
        },
        getTaskHistoryRequestAction: (
            state,
            _action: PayloadAction<GetTaskHistoryRequestPayload>
        ) => {
            state.taskHistory.loading = true;
        },
        getOrganisationTasksHistoryRequestAction: (
            state,
            _action: PayloadAction<GetTaskHistoryRequestPayload>
        ) => {
            state.taskHistory.loading = true;
        },
        getTaskHistorySuccessAction: (
            state,
            action: PayloadAction<{
                data: TaskBatch[];
                pageData: PageData;
            }>
        ) => {
            //if (action.payload.pageData.currentPage === 0) {
            //    state.taskHistory.data = action.payload.data;
            //} else {
            //    state.taskHistory.data = uniqWith(
            //        [...state.taskHistory.data, ...action.payload.data],
            //        isEqual
            //    );
            //}
            // Always replace: each page fetch becomes the current table rows
            state.taskHistory.data = action.payload.data;

            state.taskHistory.loading = false;
            state.taskHistory.pageData = action.payload.pageData;
            state.taskHistory.errorMessages = initialState.taskHistory.errorMessages;
        },
        getTaskHistoryErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.taskHistory.loading = false;
            state.taskHistory.data = initialState.taskHistory.data;
            state.taskHistory.errorMessages = action.payload;
        },
        updateTaskHistoryFiltersAction: (
            state,
            action: PayloadAction<GetTaskHistoryRequestPayload['filters']>
        ) => {
            state.taskHistory.filters = action.payload;
        },
        updateTaskHistorySortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetTaskHistoryRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.taskHistory.sortBy = action.payload.sortBy;
            state.taskHistory.sortAscending = action.payload.sortAscending;
        },
        updateTaskHistoryTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.taskHistory.taskTableFilter = action.payload;
        },
        clearTaskHistoryStateAllTableFiltersAction: (state) => {
            state.taskHistory.filters = initialState.taskHistory.filters;
            state.taskHistory.sortBy = initialState.taskHistory.sortBy;
            state.taskHistory.sortAscending = initialState.taskHistory.sortAscending;
            state.taskHistory.taskTableFilter = initialState.taskHistory.taskTableFilter;
        },
        clearTaskHistoryStateDataAction: (state) => {
            state.taskHistory.filters = initialState.taskHistory;
        },
        setSelectedTaskHistoryIdRequestAction: (
            state,
            _action: PayloadAction<SetSelectedTaskHistoryRequestPayload>
        ) => {
            state.taskHistory.activeData.selectedId = initialState.taskHistory.activeData.selectedId;
            state.taskHistory.activeData.companyId = initialState.taskHistory.activeData.companyId;
        },
        setSelectedTaskHistoryIdSuccessAction: (
            state,
            action: PayloadAction<SetSelectedTaskHistoryPayload>
        ) => {
            state.taskHistory.activeData.selectedId = action.payload.BatchId;
            state.taskHistory.activeData.companyId = action.payload.CompanyId;
        },
        getTaskHistoryDataRequestAction: (
            state,
            _action: PayloadAction<string>
        ) => {
            state.taskHistory.activeData.record = initialState.taskHistory.activeData.record;
            state.taskHistory.activeData.loading = true;
        },
        getTaskHistoryDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.taskHistory.activeData.record = action.payload.record;
            state.taskHistory.activeData.loading = false;
            state.taskHistory.activeData.errorMessages = initialState.taskHistory.activeData.errorMessages;
        },
        getTaskHistoryDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.taskHistory.activeData.record = initialState.taskHistory.activeData.record;
            state.taskHistory.activeData.loading = false;
            state.taskHistory.activeData.errorMessages = action.payload;
        },
        clearTaskHistoryDataAction: (state) => {
            state.taskHistory.activeData = initialState.taskHistory.activeData;
        },
        getActionedTicketDetailsRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    batchId: string;
                    companyId: string | undefined;
                }>
            ) => {
                state.taskHistory.activeData.record = initialState.taskHistory.activeData.record;
                state.taskHistory.activeData.loading = true;
            },
            prepare: (
                batchId: string,
                companyId: string | undefined
            ) => ({
                payload: {
                    batchId,
                    companyId
                }
            })
        },
        getActionedPaymentDetailsRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    batchId: string;
                    companyId: string | undefined;
                }>
            ) => {
                state.taskHistory.activeData.record = initialState.taskHistory.activeData.record;
                state.taskHistory.activeData.loading = true;
            },
            prepare: (
                batchId: string,
                companyId: string | undefined
            ) => ({
                payload: {
                    batchId,
                    companyId
                }
            })
        },
        getActionedPhoneCallDataRequestAction: (
            state,
            _action: PayloadAction<GetActionedPhoneCallDataRequest>
        ) => {
            state.taskHistory.activeData.record = initialState.taskHistory.activeData.record;
            state.taskHistory.activeData.loading = true;
        },
        getActionedPaymentPlanDetailsRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    batchId: string;
                    companyId: string | undefined;
                }>
            ) => {
                state.taskHistory.activeData.record = initialState.taskHistory.activeData.record;
                state.taskHistory.activeData.loading = true;
            },
            prepare: (
                batchId: string,
                companyId: string | undefined
            ) => ({
                payload: {
                    batchId,
                    companyId
                }
            })
        },
        getActionedPaymentBehaviourInsightRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    batchId: string;
                    companyId: string | undefined;
                }>
            ) => {
                state.taskHistory.activeData.record = initialState.taskHistory.activeData.record;
                state.taskHistory.activeData.loading = true;
            },
            prepare: (
                batchId: string,
                companyId: string | undefined
            ) => ({
                payload: {
                    batchId,
                    companyId
                }
            })
        },
        setSelectedTaskHistoryNotificationIdRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    taskId: string | null;
                    callback: () => void;
                }>
            ) => {
                state.taskHistory.activeData.notifications.activeData.selectedId = initialState.taskHistory.activeData.notifications.activeData.selectedId;
            },
            prepare: (
                taskId: string | null,
                callback: () => void
            ) => ({
                payload: {
                    taskId,
                    callback
                }
            })
        },
        setSelectedTaskHistoryNotificationIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.taskHistory.activeData.notifications.activeData.selectedId = action.payload;
        },
        getTaskHistoryNotificationsRequestAction: (
            state,
            _action: PayloadAction<GetTaskHistoryNotificationsRequestPayload>
        ) => {
            state.taskHistory.activeData.notifications.loading = true;
        },
        getTaskHistoryNotificationsSuccessAction: (
            state,
            action: PayloadAction<{
                data: TaskHistoryNotification[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.taskHistory.activeData.notifications.data = action.payload.data;
            } else {
                state.taskHistory.activeData.notifications.data = uniqWith(
                    [
                        ...state.taskHistory.activeData.notifications.data,
                        ...action.payload.data
                    ],
                    isEqual
                );
            }

            state.taskHistory.activeData.notifications.loading = false;
            state.taskHistory.activeData.notifications.pageData = action.payload.pageData;
            state.taskHistory.activeData.notifications.errorMessages = initialState.taskHistory.activeData.notifications.errorMessages;
        },
        getTaskHistoryNotificationsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.taskHistory.activeData.notifications.loading = false;
            state.taskHistory.activeData.notifications.data = initialState.taskHistory.activeData.notifications.data;
            state.taskHistory.activeData.notifications.errorMessages = action.payload;
        },
        updateTaskHistoryNotificationsFiltersAction: (
            state,
            action: PayloadAction<GetTaskHistoryNotificationsRequestPayload['filters']>
        ) => {
            state.taskHistory.activeData.notifications.filters = action.payload;
        },
        updateTaskHistoryDeliveryReportSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetTaskHistoryDeliveryReportRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.taskHistory.activeData.detailsDelivery.sortBy = action.payload.sortBy;
            state.taskHistory.activeData.detailsDelivery.sortAscending = action.payload.sortAscending;
        },
        getTaskHistoryDeliveryReportRequestAction: (
            state,
            _action: PayloadAction<GetTaskHistoryDeliveryReportRequestPayload>
        ) => {
            state.taskHistory.activeData.detailsDelivery.loading = true;
        },
        updateTaskHistoryDeliveryReportFiltersAction: (
            state,
            action: PayloadAction<GetTaskHistoryDeliveryReportRequestPayload['filters']>
        ) => {
            state.taskHistory.activeData.detailsDelivery.filters = action.payload;
        },
        getTaskHistoryDeliveryReportErrorAction: (
            state,
            _action: PayloadAction<string[]>
        ) => {
            state.taskHistory.activeData.detailsDelivery.loading = false;
            state.taskHistory.activeData.detailsDelivery.data = initialState.taskHistory.activeData.detailsDelivery.data;
        },
        getTaskHistoryDeliveryReportSuccessAction: (
            state,
            action: PayloadAction<{
                data: TaskDeliveryReport[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.taskHistory.activeData.detailsDelivery.data = action.payload.data;
            } else {
                state.taskHistory.activeData.detailsDelivery.data = uniqWith(
                    [
                        ...state.taskHistory.activeData.detailsDelivery.data,
                        ...action.payload.data
                    ],
                    isEqual
                );
            }

            state.taskHistory.activeData.detailsDelivery.loading = false;
            state.taskHistory.activeData.detailsDelivery.pageData = action.payload.pageData;
        },
        updateTaskHistoryNotificationsSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetTaskHistoryNotificationsRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.taskHistory.activeData.notifications.sortBy = action.payload.sortBy;
            state.taskHistory.activeData.notifications.sortAscending = action.payload.sortAscending;
        },
        getOrganisationTaskHistoryNotificationDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    companyId: string | undefined;
                    companyIds: (string | undefined)[];
                    notificationId: string;
                    conversionCurrency: number | undefined;
                    isUsingCloudImportType: boolean;
                }>
            ) => {
                state.taskHistory.activeData.notifications.activeData.record = initialState.taskHistory.activeData.notifications.activeData.record;
                state.taskHistory.activeData.notifications.activeData.loading = true;
            },
            prepare: (
                companyId: string | undefined,
                notificationId: string,
                conversionCurrency: number | undefined,
                isUsingCloudImportType: boolean,
                companyIds: (string | undefined)[] = []
            ) => ({
                payload: {
                    companyId,
                    companyIds,
                    notificationId,
                    conversionCurrency,
                    isUsingCloudImportType
                }
            })
        },
        getTaskHistoryNotificationDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    notificationId: string;
                    isUsingCloudImportType: boolean;
                }>
            ) => {
                state.taskHistory.activeData.notifications.activeData.record = initialState.taskHistory.activeData.notifications.activeData.record;
                state.taskHistory.activeData.notifications.activeData.loading = true;
            },
            prepare: (
                notificationId: string,
                isUsingCloudImportType: boolean
            ) => ({
                payload: {
                    notificationId,
                    isUsingCloudImportType

                }
            })
        },
        getOrganisationTaskHistoryNotificationDataSuccessAction: (
            state,
            action: PayloadAction<{record: {}}>
        ) => {
            state.taskHistory.activeData.notifications.activeData.record = action.payload.record;
            state.taskHistory.activeData.notifications.activeData.loading = false;
            state.taskHistory.activeData.notifications.activeData.errorMessages = initialState.taskHistory.activeData.notifications.activeData.errorMessages;
        },
        getTaskHistoryNotificationDataSuccessAction: (
            state,
            action: PayloadAction<{record: {}}>
        ) => {
            state.taskHistory.activeData.notifications.activeData.record = action.payload.record;
            state.taskHistory.activeData.notifications.activeData.loading = false;
            state.taskHistory.activeData.notifications.activeData.errorMessages = initialState.taskHistory.activeData.notifications.activeData.errorMessages;
        },
        getOrganisationTaskHistoryNotificationDataErrorAction: (
            state,
            _action: PayloadAction<string[]>
        ) => {
            state.taskHistory.activeData.notifications.activeData.record = initialState.taskHistory.activeData.notifications.activeData.record;
            state.taskHistory.activeData.notifications.activeData.loading = false;
        },
        getTaskHistoryNotificationDataErrorAction: (
            state,
            _action: PayloadAction<string[]>
        ) => {
            state.taskHistory.activeData.notifications.activeData.record = initialState.taskHistory.activeData.notifications.activeData.record;
            state.taskHistory.activeData.notifications.activeData.loading = false;
        },
        clearTaskHistoryNotificationDataAction: (state) => {
            state.taskHistory.activeData.notifications.activeData = initialState.taskHistory.activeData.notifications.activeData;
        },
        getOrganisationTaskHistoryNotificationInvoicesRequestAction: (
            state,
            _action: PayloadAction<GetExtendedInvoicesRequestPayload>
        ) => {
            state.taskHistory.activeData.notifications.activeData.invoices.loading = true;
        },
        getTaskHistoryNotificationInvoicesRequestAction: (
            state,
            _action: PayloadAction<GetExtendedInvoicesRequestPayload>
        ) => {
            state.taskHistory.activeData.notifications.activeData.invoices.loading = true;
        },
        getTaskHistoryNotificationInvoicesSuccessAction: (
            state,
            action: PayloadAction<{
                data: Invoice[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.taskHistory.activeData.notifications.activeData.invoices.data = action.payload.data;
            } else {
                state.taskHistory.activeData.notifications.activeData.invoices.data = uniqWith(
                    [
                        ...state.taskHistory.activeData.notifications.activeData.invoices.data,
                        ...action.payload.data
                    ],
                    isEqual
                );
            }

            state.taskHistory.activeData.notifications.activeData.invoices.loading = false;
            state.taskHistory.activeData.notifications.activeData.invoices.pageData = action.payload.pageData;
            state.taskHistory.activeData.notifications.activeData.invoices.errorMessages = initialState.taskHistory.activeData.notifications.activeData.invoices.errorMessages;
        },
        getTaskHistoryNotificationInvoicesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.taskHistory.activeData.notifications.activeData.invoices.loading = false;
            state.taskHistory.activeData.notifications.activeData.invoices.data = initialState.taskHistory.activeData.notifications.activeData.invoices.data;
            state.taskHistory.activeData.notifications.activeData.invoices.errorMessages = action.payload;
        },
        updateTaskHistoryNotificationInvoicesFiltersAction: (
            state,
            action: PayloadAction<GetExtendedInvoicesRequestPayload['filters']>
        ) => {
            state.taskHistory.activeData.notifications.activeData.invoices.filters = action.payload;
        },
        updateTaskHistoryNotificationInvoicesSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetExtendedInvoicesRequestPayload['sortBy'];
                sortAscending: boolean;
                invoiceState: string;
            }>
        ) => {
            state.taskHistory.activeData.notifications.activeData.invoices.sortBy = action.payload.sortBy;
            state.taskHistory.activeData.notifications.activeData.invoices.sortAscending = action.payload.sortAscending;
            state.taskHistory.activeData.notifications.activeData.invoices.invoiceState = action.payload.invoiceState;
        },
        getTaskHistoryNotificationTasksRequestAction: (
            state,
            _action: PayloadAction<GetNotificationTasksRequestPayload>
        ) => {
            state.taskHistory.activeData.notifications.activeData.tasks.loading = true;
        },
        getTaskHistoryNotificationTasksSuccessAction: (
            state,
            action: PayloadAction<{
                data: TaskHistory[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.taskHistory.activeData.notifications.activeData.tasks.data = action.payload.data;
            } else {
                state.taskHistory.activeData.notifications.activeData.tasks.data = uniqWith(
                    [
                        ...state.taskHistory.activeData.notifications.activeData.tasks.data,
                        ...action.payload.data
                    ],
                    isEqual
                );
            }

            state.taskHistory.activeData.notifications.activeData.tasks.loading = false;
            state.taskHistory.activeData.notifications.activeData.tasks.pageData = action.payload.pageData;
            state.taskHistory.activeData.notifications.activeData.tasks.errorMessages = initialState.taskHistory.activeData.notifications.activeData.tasks.errorMessages;
        },
        getTaskHistoryNotificationTasksErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.taskHistory.activeData.notifications.activeData.tasks.loading = false;
            state.taskHistory.activeData.notifications.activeData.tasks.data = initialState.taskHistory.activeData.notifications.activeData.tasks.data;
            state.taskHistory.activeData.notifications.activeData.tasks.errorMessages = action.payload;
        },
        updateTaskHistoryNotificationTasksFiltersAction: (
            state,
            action: PayloadAction<GetNotificationTasksRequestPayload['filters']>
        ) => {
            state.taskHistory.activeData.notifications.activeData.tasks.filters = action.payload;
        },
        updateTaskHistoryNotificationTasksSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetNotificationTasksRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.taskHistory.activeData.notifications.activeData.tasks.sortBy = action.payload.sortBy;
            state.taskHistory.activeData.notifications.activeData.tasks.sortAscending = action.payload.sortAscending;
        },
        getTaskHistoryActionedTasksRequestAction: (
            state,
            _action: PayloadAction<GetTaskHistoryActionedTasksRequestPayload>
        ) => {
            state.taskHistory.activeData.changedTasks.list.loading = true;
        },
        getTaskHistoryChangedTasksRequestAction: (
            state,
            _action: PayloadAction<GetTaskHistoryChangedTasksRequestPayload>
        ) => {
            state.taskHistory.activeData.changedTasks.list.loading = true;
        },
        getTaskHistoryChangedTasksSuccessAction: (
            state,
            action: PayloadAction<{
                data: Task[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.taskHistory.activeData.changedTasks.list.data = action.payload.data;
            } else {
                state.taskHistory.activeData.changedTasks.list.data = uniqWith(
                    [
                        ...state.taskHistory.activeData.changedTasks.list.data,
                        ...action.payload.data
                    ],
                    isEqual
                );
            }

            state.taskHistory.activeData.changedTasks.list.loading = false;
            state.taskHistory.activeData.changedTasks.list.pageData = action.payload.pageData;
            state.taskHistory.activeData.changedTasks.list.errorMessages = initialState.taskHistory.activeData.changedTasks.list.errorMessages;
        },
        getTaskHistoryChangedTasksErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.taskHistory.activeData.changedTasks.list.loading = false;
            state.taskHistory.activeData.changedTasks.list.data = initialState.taskHistory.activeData.changedTasks.list.data;
            state.taskHistory.activeData.changedTasks.list.errorMessages = action.payload;
        },
        getTaskHistoryTasksFailedRequestAction: (
            state,
            _action: PayloadAction<GetTaskHistoryTasksFailedRequestPayload>
        ) => {
            state.taskHistory.activeData.failedList.loading = false;
        },
        getTaskHistoryTasksFailedSuccessAction: (
            state,
            action: PayloadAction<{
                data: Task[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.taskHistory.activeData.failedList.data = action.payload.data;
            } else {
                state.taskHistory.activeData.failedList.data = uniqWith(
                    [
                        ...state.taskHistory.activeData.failedList.data,
                        ...action.payload.data
                    ],
                    isEqual
                );
            }

            state.taskHistory.activeData.failedList.loading = false;
            state.taskHistory.activeData.failedList.pageData = action.payload.pageData;
            state.taskHistory.activeData.failedList.errorMessages = initialState.taskHistory.activeData.failedList.errorMessages;
        },
        getTaskHistoryTasksFailedErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.taskHistory.activeData.failedList.loading = false;
            state.taskHistory.activeData.failedList.data = initialState.taskHistory.activeData.failedList.data;
            state.taskHistory.activeData.failedList.errorMessages = action.payload;
        },
        getTaskHistoryActionedTasksDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    batchId: string | null;
                    companyId: string | null;
                }>
            ) => {
                state.taskHistory.activeData.changedTasks.activeData.loading = true;
            },
            prepare: (
                batchId: string | null,
                companyId: string | null
            ) => ({
                payload: {
                    batchId,
                    companyId

                }
            })
        },
        getTaskHistoryChangedTasksDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    batchId: string | null;
                    companyId: string | undefined;
                }>
            ) => {
                state.taskHistory.activeData.changedTasks.activeData.loading = true;
            },
            prepare: (
                batchId: string | null,
                companyId: string | undefined
            ) => ({
                payload: { batchId, companyId }
            })
        },
        getTaskHistoryChangedTasksDataSuccessAction: (
            state,
            action: PayloadAction<{record: {}}>
        ) => {
            state.taskHistory.activeData.changedTasks.activeData.record = action.payload.record;
            state.taskHistory.activeData.changedTasks.activeData.loading = false;
            state.taskHistory.activeData.changedTasks.activeData.errorMessages = initialState.taskHistory.activeData.changedTasks.activeData.errorMessages;
        },
        getTaskHistoryChangedTasksDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.taskHistory.activeData.changedTasks.activeData.record = initialState.taskHistory.activeData.changedTasks.activeData.record;
            state.taskHistory.activeData.changedTasks.activeData.loading = false;
            state.taskHistory.activeData.changedTasks.activeData.errorMessages = action.payload;
        },
        bulkSaveTaskDetailsUpdateRequestAction: (
            state,
            _action: PayloadAction<BulkSaveTaskDetailsUpdateRequestPayload>
        ) => {
            state.activeTasks.saveTaskDataLoading = true;
        },
        organisationBulkSaveTaskDetailsUpdateRequestAction: (
            state,
            _action: PayloadAction<BulkSaveTaskDetailsUpdateRequestPayload>
        ) => {
            state.activeTasks.saveTaskDataLoading = true;
        },
        bulkSaveTaskDetailsUpdateResponseAction: (state) => {
            state.activeTasks.saveTaskDataLoading = false;
        },
        getTaskHistoryCollectionsDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    batchId: string | null;
                    companyId: string | undefined;
                }>
            ) => {
                state.taskHistory.activeData.collections.activeData.loading = true;
            },
            prepare: (
                batchId: string | null,
                companyId: string | undefined
            ) => ({
                payload: { batchId, companyId }
            })
        },
        getTaskHistoryCollectionsDataSuccessAction: (
            state,
            action: PayloadAction<{record: {}}>
        ) => {
            state.taskHistory.activeData.collections.activeData.record = action.payload.record;
            state.taskHistory.activeData.collections.activeData.loading = false;
            state.taskHistory.activeData.collections.activeData.errorMessages = initialState.taskHistory.activeData.collections.activeData.errorMessages;
        },
        getTaskHistoryCollectionsDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.taskHistory.activeData.collections.activeData.record = initialState.taskHistory.activeData.collections.activeData.record;
            state.taskHistory.activeData.collections.activeData.loading = false;
            state.taskHistory.activeData.collections.activeData.errorMessages = action.payload;
        },
        getTaskHistoryCollectionTasksRequestAction: (
            state,
            _action: PayloadAction<GetTaskHistoryCollectionTasksRequestPayload>
        ) => {
            state.taskHistory.activeData.collections.actionedTasks.loading = true;
        },
        getTaskHistoryCollectionsTaskDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state
            .taskHistory
            .activeData
            .collections
            .actionedTasks
            .activeData
            .record = initialState.taskHistory.activeData.collections.actionedTasks.activeData.record;

            state
            .taskHistory
            .activeData
            .collections
            .actionedTasks
            .activeData
            .loading = false;

            state
            .taskHistory
            .activeData
            .collections
            .actionedTasks
            .activeData
            .errorMessages = action.payload;
        },
        getTaskHistoryCollectionsTaskDataSuccessAction: (
            state,
            action: PayloadAction<{record: {}}>
        ) => {
            state
            .taskHistory
            .activeData
            .collections
            .actionedTasks
            .activeData
            .record = action.payload.record;

            state
            .taskHistory
            .activeData
            .collections
            .actionedTasks
            .activeData
            .loading = false;

            state
            .taskHistory
            .activeData
            .collections
            .actionedTasks
            .activeData
            .errorMessages = initialState.taskHistory.activeData.collections.actionedTasks.activeData.errorMessages;
        },
        getTaskHistoryCollectionTasksErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.taskHistory.activeData.collections.actionedTasks.loading = false;
            state.taskHistory.activeData.collections.actionedTasks.data = initialState.taskHistory.activeData.collections.actionedTasks.data;
            state.taskHistory.activeData.collections.actionedTasks.errorMessages = action.payload;
        },
        getTaskHistoryCollectionTasksSuccessAction: (
            state,
            action: PayloadAction<{
                data: Task[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.taskHistory.activeData.collections.actionedTasks.data = action.payload.data;
            } else {
                state.taskHistory.activeData.collections.actionedTasks.data = uniqWith(
                    [
                        ...state.taskHistory.activeData.collections.actionedTasks.data,
                        ...action.payload.data
                    ],
                    isEqual
                );
            }

            state.taskHistory.activeData.collections.actionedTasks.loading = false;
            state.taskHistory.activeData.collections.actionedTasks.pageData = action.payload.pageData;
            state.taskHistory.activeData.collections.actionedTasks.errorMessages = initialState.taskHistory.activeData.collections.actionedTasks.errorMessages;
        },
        setSelectedTaskHistoryCollectionsTaskIdSuccessAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.taskHistory.activeData.collections.actionedTasks.activeData.selectedId = action.payload;
        },
        getTaskViewerDetailForUserSuccessAction: (
            state,
            action: PayloadAction<User>
        ) => {
            state.activeTasks.viewingUser.user = action.payload;
            state.activeTasks.viewingUser.loading = false;
        },
        getTaskViewerDetailForUserRequestAction: (
            state,
            action: PayloadAction<GetTaskViewerDetailForUserRequestPayload>
        ) => {
            const { silent } = action.payload;
            if (silent) {
                return state;
            }
            
            state.activeTasks.viewingUser.loading = true;
        },
        getTaskViewerDetailForUserErrorAction: (
            state,
            _action: PayloadAction<string[]>
        ) => {
            state.activeTasks.viewingUser.loading = false;
            state.activeTasks.viewingUser.user = undefined;
        },
        getTaskWorkflowsRequestAction: (
            state,
            _action: PayloadAction<TaskWorkflowFilter[]>
        ) => {
            state.taskWorkflows.loading = true;
        },
        getTaskWorkflowsSuccessAction: (
            state,
            action: PayloadAction<DynamicObject[]>
        ) => {
            state.taskWorkflows.data = action.payload;
            state.taskWorkflows.loading = false;
            state.taskWorkflows.errorMessages = initialState.taskWorkflows.errorMessages;
        },
        getTaskWorkflowsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.taskWorkflows.loading = false;
            state.taskWorkflows.data = initialState.taskWorkflows.data;
            state.taskWorkflows.errorMessages = action.payload;
        },
        getTaskViewerDetailForUserOrganisationRequestAction: (
            state,
            action: PayloadAction<GetTaskViewerDetailForUserRequestPayload>
        ) => {
            const { silent } = action.payload;
            if (silent) {
                return state;
            }

            state.activeTasks.viewingUser.loading = true;
        },
        getTaskHistoryCollectionsTaskDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    companyId: string | undefined,
                    taskId: string,
                    isUsingCloudImportType: boolean
                }>
            ) => {
                state.taskHistory.activeData.collections.actionedTasks.activeData.record 
                    = initialState.taskHistory.activeData.collections.actionedTasks.activeData.record;
                state.taskHistory.activeData.collections.actionedTasks.activeData.loading = true;
            },
            prepare: (
                companyId: string | undefined,
                notificationId: string,
                isUsingCloudImportType: boolean
            ) => {
                return {
                    payload: {
                        companyId,
                        taskId: notificationId,
                        isUsingCloudImportType
                    }
                };
            }
        },
        clearTaskHistoryCollectionsTaskDataAction: (state) => {
            state.taskHistory.activeData.collections.actionedTasks.activeData 
                = initialState.taskHistory.activeData.collections.actionedTasks.activeData;
        },
        setSelectedTaskHistoryCollectionsTaskIdRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    taskId: string | null,
                    callback: () => void
                }>
            ) => {
                state.taskHistory.activeData.collections.actionedTasks.activeData.selectedId 
                    = initialState.taskHistory.activeData.collections.actionedTasks.activeData.selectedId;
            },
            prepare: (
                taskId: string | null,
                callback: () => void
            ) => {
                return {
                    payload: {
                        taskId,
                        callback
                    }
                }
            }
        },
        getActionedCancelPaymentPlanDetailsRequestAction: (
            state,
            _action: PayloadAction<GetTaskHistoryCancelPaymentPlanRequestPayload>
        ) => {
            state.taskHistory.activeData.cancelledPaymentPlanTasks.list.loading = true;
        },
        getTaskHistoryCancelledPaymentPlanTasksSuccessAction: (
            state,
            action: PayloadAction<{
                data: any[],
                pageData: PageData
            }>
        ) => {
            const { data, pageData } = action.payload;
            const listState = state.taskHistory.activeData.cancelledPaymentPlanTasks.list;

            if (pageData.currentPage === 0) {
                listState.data = data;
            } else {
                // Append + dedupe
                listState.data = uniqWith(concat(listState.data, data), isEqual);
            }

            listState.loading = false;
            listState.pageData = pageData;
            listState.errorMessages = [];
        },
        getTaskHistoryCancelledPaymentPlanTasksErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            const listState = state.taskHistory.activeData.cancelledPaymentPlanTasks.list;

            listState.loading = false;
            listState.data = initialState.taskHistory.activeData.cancelledPaymentPlanTasks.list.data;
            listState.errorMessages = action.payload;
        },
    }
});

const { actions, reducer } = taskSlice;

// Actions
export const {
    setSelectedTaskHistoryCollectionsTaskIdRequestAction,
    clearTaskHistoryCollectionsTaskDataAction,
    getTasksForOrganisationRequestAction,
    getTasksRequestAction,
    getTasksSuccessAction,
    getTasksErrorAction,
    updateTasksFiltersAction,
    updateTasksSortByAndStatusAction,
    updateTasksTableFilterAction,
    clearTasksStateAllTableFiltersAction,
    clearTasksStateDataAction,
    getTasksActionFilterOptionsRequestAction,
    getTasksActionFilterOptionsResponseAction,
    getTableCompactViewRequestAction,
    getTableCompactViewSuccessAction,
    setTableCompactViewRequestAction,
    setTableCompactViewSuccessAction,
    setTaskExpandStatusAction,
    setSelectedTaskIdRequestAction,
    setSelectedTaskIdSuccessAction,
    getTaskDataForOrganisationRequestAction,
    getTaskDataRequestAction,
    getTaskDataSuccessAction,
    getTaskDataErrorAction,
    clearTaskDataAction,
    notifyCustomersRequestAction,
    notifyOrganisationCustomersRequestAction,
    notifyCustomersResponseAction,
    getTaskTicketInvoicesRequestAction,
    getTaskTicketInvoicesSuccessAction,
    getTaskTicketInvoicesErrorAction,
    updateTaskTicketInvoicesFiltersAction,
    updateTaskTicketInvoicesTableFiltersAction,
    updateTaskTicketInvoicesSortByAndStateAction,
    getTaskPaymentPlanScheduleRequestAction,
    getTaskPaymentPlanScheduleSuccessAction,
    getTaskPaymentPlanScheduleErrorAction,
    updateTaskPaymentPlanScheduleFiltersAction,
    updateTaskPaymentPlanScheduleSortByAndStateAction,
    getTaskHistoryRequestAction,
    getOrganisationTasksHistoryRequestAction,
    getTaskHistorySuccessAction,
    getTaskHistoryErrorAction,
    updateTaskHistoryFiltersAction,
    updateTaskHistorySortByAction,
    updateTaskHistoryTableFilterAction,
    clearTaskHistoryStateAllTableFiltersAction,
    clearTaskHistoryStateDataAction,
    setSelectedTaskHistoryIdRequestAction,
    setSelectedTaskHistoryIdSuccessAction,
    getTaskHistoryDataRequestAction,
    getTaskHistoryDataSuccessAction,
    getTaskHistoryDataErrorAction,
    clearTaskHistoryDataAction,
    getActionedTicketDetailsRequestAction,
    getActionedPaymentDetailsRequestAction,
    getActionedPhoneCallDataRequestAction,
    getActionedPaymentPlanDetailsRequestAction,
    getActionedPaymentBehaviourInsightRequestAction,
    setSelectedTaskHistoryNotificationIdRequestAction,
    setSelectedTaskHistoryNotificationIdSuccessAction,
    getTaskHistoryNotificationsRequestAction,
    getTaskHistoryNotificationsSuccessAction,
    getTaskHistoryNotificationsErrorAction,
    updateTaskHistoryNotificationsFiltersAction,
    updateTaskHistoryDeliveryReportSortByAction,
    getTaskHistoryDeliveryReportRequestAction,
    updateTaskHistoryDeliveryReportFiltersAction,
    getTaskHistoryDeliveryReportErrorAction,
    getTaskHistoryDeliveryReportSuccessAction,
    updateTaskHistoryNotificationsSortByAction,
    getOrganisationTaskHistoryNotificationDataRequestAction,
    getTaskHistoryNotificationDataRequestAction,
    getOrganisationTaskHistoryNotificationDataSuccessAction,
    getTaskHistoryNotificationDataSuccessAction,
    getOrganisationTaskHistoryNotificationDataErrorAction,
    getTaskHistoryNotificationDataErrorAction,
    clearTaskHistoryNotificationDataAction,
    getOrganisationTaskHistoryNotificationInvoicesRequestAction,
    getTaskHistoryNotificationInvoicesRequestAction,
    getTaskHistoryNotificationInvoicesSuccessAction,
    getTaskHistoryNotificationInvoicesErrorAction,
    updateTaskHistoryNotificationInvoicesFiltersAction,
    updateTaskHistoryNotificationInvoicesSortByAndStateAction,
    getTaskHistoryNotificationTasksRequestAction,
    getTaskHistoryNotificationTasksSuccessAction,
    getTaskHistoryNotificationTasksErrorAction,
    updateTaskHistoryNotificationTasksFiltersAction,
    updateTaskHistoryNotificationTasksSortByAction,
    getTaskHistoryActionedTasksRequestAction,
    getTaskHistoryChangedTasksRequestAction,
    getTaskHistoryChangedTasksSuccessAction,
    getTaskHistoryChangedTasksErrorAction,
    getTaskHistoryTasksFailedRequestAction,
    getTaskHistoryTasksFailedSuccessAction,
    getTaskHistoryTasksFailedErrorAction,
    getTaskHistoryActionedTasksDataRequestAction,
    getTaskHistoryChangedTasksDataRequestAction,
    getTaskHistoryChangedTasksDataSuccessAction,
    getTaskHistoryChangedTasksDataErrorAction,
    bulkSaveTaskDetailsUpdateRequestAction,
    organisationBulkSaveTaskDetailsUpdateRequestAction,
    bulkSaveTaskDetailsUpdateResponseAction,
    getTaskHistoryCollectionsDataRequestAction,
    getTaskHistoryCollectionsDataSuccessAction,
    getTaskHistoryCollectionsDataErrorAction,
    getTaskHistoryCollectionTasksRequestAction,
    getTaskHistoryCollectionsTaskDataErrorAction,
    getTaskHistoryCollectionsTaskDataSuccessAction,
    getTaskHistoryCollectionTasksErrorAction,
    getTaskHistoryCollectionTasksSuccessAction,
    setSelectedTaskHistoryCollectionsTaskIdSuccessAction,
    getTaskViewerDetailForUserSuccessAction,
    getTaskViewerDetailForUserRequestAction,
    getTaskViewerDetailForUserErrorAction,
    getTaskWorkflowsRequestAction,
    getTaskWorkflowsSuccessAction,
    getTaskWorkflowsErrorAction,
    getTaskViewerDetailForUserOrganisationRequestAction,
    getTaskHistoryCollectionsTaskDataRequestAction,
    getActionedCancelPaymentPlanDetailsRequestAction,
    getTaskHistoryCancelledPaymentPlanTasksSuccessAction,
    getTaskHistoryCancelledPaymentPlanTasksErrorAction
} = actions;

export const processTaskCompactViewReduxSettingAction = createAction(
    `${actionPrefix}/processTaskCompactViewReduxSettingAction`,
    function prepare(userPrefs: DynamicObject[]) {
        return {
            payload: userPrefs
        };
    }
);

export const tasksFetchedForOrganisationAction = createAction(
    `${actionPrefix}/tasksFetchedForOrganisationAction`,
    function prepare(payload: TasksFetchedForOrganisationPayload) {
        return {
            payload
        };
    }
);

export const getTaskEditPropertiesRequestAction = createAction(
    `${actionPrefix}/getTaskEditPropertiesRequestAction`,
    function prepare(payload: GetTaskPropertiesRequestPayload) {
        return {
            payload
        };
    }
);

export const getWorkflowStepOptionsRequestAction = createAction(
    `${actionPrefix}/getWorkflowStepOptionsRequestAction`,
    function prepare(payload: GetWorkflowStepOptionsRequestPayload) {
        return {
            payload
        };
    }
);

export const getTriggersForWorkflowRequestAction = createAction(
    `${actionPrefix}/getTriggersForWorkflowRequestAction`,
    function prepare(
        workflowId: string,
        stateName: string,
        companyId: string,
        callback?: (response: any) => void
    ) {
        return {
            payload: {
                workflowId,
                stateName,
                companyId,
                callback
            }
        };
    }
);

export const taskOrganisationPaymentBehaviorInsightsRequestAction = createAction(
    `${actionPrefix}/taskOrganisationPaymentBehaviorInsightsRequestAction`,
    function prepare(payload: {}) {
        return {
            payload
        };
    }
);

export const taskPaymentBehaviorInsightsRequestAction = createAction(
    `${actionPrefix}/taskPaymentBehaviorInsightsRequestAction`,
    function prepare(payload: {}) {
        return {
            payload
        };
    }
);

export const escalateToCollectionsAction = createAction(
    `${actionPrefix}/escalateToCollectionsAction`,
    function prepare(payload: {}) {
        return {
            payload
        };
    }
);

export const organisationEscalateToCollectionsAction = createAction(
    `${actionPrefix}/organisationEscalateToCollectionsAction`,
    function prepare(payload: {}) {
        return {
            payload
        };
    }
);

export const taskActionPaymentVerificationTaskRequest = createAction(
    `${actionPrefix}/taskActionPaymentVerificationTaskRequest`,
    function prepare(payload: TaskActionPaymentVerificationTaskRequestPayload) {
        return {
            payload
        };
    }
);

export const taskActionOrganisationPaymentVerificationTaskRequest = createAction(
    `${actionPrefix}/taskActionOrganisationPaymentVerificationTaskRequest`,
    function prepare(payload: TaskActionPaymentVerificationTaskRequestPayload) {
        return {
            payload
        };
    }
);

export const taskActionPhoneCallTaskRequest = createAction(
    `${actionPrefix}/taskActionPhoneCallTaskRequest`,
    function prepare(
        payload: TaskActionPhoneCallTaskRequest,
        callback: (response: any) => void
    ) {
        return {
            payload: {
                ...payload,
                callback
            }
        };
    }
);

export const taskActionOrganisationPhoneCallTaskRequest = createAction(
    `${actionPrefix}/taskActionOrganisationPhoneCallTaskRequest`,
    function prepare(
        payload: TaskActionPhoneCallTaskRequest,
        callback: (response: any) => void
    ) {
        return {
            payload: {
                payload,
                callback
            }
        };
    }
);

export const addTaskViewingsRequestAction = createAction(
    `${actionPrefix}/addTaskViewingsRequestAction`,
    function prepare(payload: ChangeTaskViewingRequestPayload) {
        return {
            payload
        };
    }
);

export const checkTasksReadyStateAction = createAction(
    `${actionPrefix}/checkTasksReadyStateAction`,
    function prepare(payload: GetTasksRequestPayload) {
        return {
            payload
        };
    }
);

export const checkTasksReadyStateActionForOrganisation = createAction(
    `${actionPrefix}/checkTasksReadyStateActionForOrganisation`,
    function prepare(payload: GetTasksRequestPayload) {
        return {
            payload
        };
    }
);

export const deleteTaskViewingsRequestAction = createAction(
    `${actionPrefix}/deleteTaskViewingsRequestAction`,
    function prepare(payload: ChangeTaskViewingRequestPayload) {
        return {
            payload
        };
    }
);

export const getTaskCustomerCustomFieldsForCompanyRequestAction = createAction(
    `${actionPrefix}/getTaskCustomerCustomFieldsForCompanyRequestAction`,
    function prepare(
        companyId: string,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                companyId,
                callback
            }
        };
    }
);

export const getTaskInvoiceCustomFieldsForCompanyRequestAction = createAction(
    `${actionPrefix}/getTaskInvoiceCustomFieldsForCompanyRequestAction`,
    function prepare(
        companyId: string,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                companyId,
                callback
            }
        };
    }
);

export const taskAddCommentRequestAction = createAction(
    `${actionPrefix}/taskAddCommentRequestAction`,
    function prepare(payload: TaskAddCommentRequestPayload) {
        return {
            payload
        }
    }
);

export const taskActionOrganisationRequestRATaskRequest = createAction(
    `${actionPrefix}/taskActionOrganisationRequestRATaskRequest`,
    function prepare(payload: TaskActionRequestOrganisationRATaskRequestPayload) {
        return {
            payload
        }
    }
);

export const getTaskCustomerCustomFieldsForOrganisationRequestAction = createAction(
    `${actionPrefix}/getTaskCustomerCustomFieldsForOrganisationRequestAction`,
    function prepare(
        CompanyIds: string[],
        FieldName?: string[],
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                CompanyIds,
                FieldName,
                callback
            }
        };
    }
);

export const getTaskInvoiceCustomFieldsForOrganisationRequestAction = createAction(
    `${actionPrefix}/getTaskInvoiceCustomFieldsForOrganisationRequestAction`,
    function prepare(
        CompanyIds: string[],
        FieldName?: string[],
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                CompanyIds,
                FieldName,
                callback
            }
        };
    }
);

export const taskActionRequestRATaskRequest = createAction(
    `${actionPrefix}/taskActionRequestRATaskRequest`,
    function prepare(payload: TaskActionRequestRATaskRequestPayload) {
        return {
            payload
        }
    }
);

export const taskActionPaymentRALinkTaskRequest = createAction(
    `${actionPrefix}/taskActionPaymentRALinkTaskRequest`,
    function prepare(payload: TaskActionPaymentRALinkTaskRequestPayload) {
        return {
            payload
        }
    }
);

export const getRemittanceAdvicesRequestAction = createAction(
    `${actionPrefix}/getRemittanceAdvicesRequestAction`,
    function prepare(payload: GetRemittanceAdvicesRequestPayload) {
        return {
            payload
        }
    }
);

export const taskActionOrganisationPaymentRALinkTaskRequest = createAction(
    `${actionPrefix}/taskActionOrganisationPaymentRALinkTaskRequest`,
    function prepare(payload: TaskActionOrganisationPaymentRALinkTaskRequestPayload) {
        return {
            payload
        }
    }
);

export const actionManualAllocatePaymentRequest = createAction(
    `${actionPrefix}/actionManualAllocatePaymentRequest`,
    function prepare(payload: {}) {
        return {
            payload
        }
    }
);

export const actionOrganisationManualAllocatePaymentRequest = createAction(
    `${actionPrefix}/actionOrganisationManualAllocatePaymentRequest`,
    function prepare(payload: {}) {
        return {
            payload
        }
    }
);

export const downloadMergedPdfRequestAction = createAction(
    `${actionPrefix}/downloadMergedPdfRequestAction`,
    function prepare(
        batchId: string,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                batchId,
                callback
            }
        }
    }
);

export const downloadMergedPdfStatusRequestAction = createAction(
    `${actionPrefix}/downloadMergedPdfStatusRequestAction`,
    function prepare(
        batchId: string,
        companyId?: string,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                batchId,
                companyId,
                callback
            }
        }
    }
);

export const getTaskHistoryBatchStatusRequestAction = createAction(
    `${actionPrefix}/getTaskHistoryBatchStatusRequestAction`,
    function prepare(
        batchId: string,
        callback?: (response: DynamicObject) => void,
        companyId?: string
    ) {
        return {
            payload: {
                batchId,
                callback,
                companyId
            }
        }
    }
);

export const getTaskHistoryQueuedTasksCountRequestAction = createAction(
    `${actionPrefix}/getTaskHistoryQueuedTasksCountRequestAction`,
    function prepare(
        batchId: string,
        callback?: (response: DynamicObject) => void,
        companyId?: string
    ) {
        return {
            payload: {
                batchId,
                callback,
                companyId
            }
        }
    }
);

export const taskActionCancelPaymentPlanRequestAction = createAction(
    `${actionPrefix}/taskActionCancelPaymentPlanRequestAction`,
    function prepare(
        payload: object
    ) {
        return {
            payload
        }
    }
);

export const getActionTaskOptionsRequestAction = createAction(
    `${actionPrefix}/getActionTaskOptionsRequestAction`,
    function prepare(
        payload: ActionTaskOptionsRequestPayload,
        callback?: (response: any) => void
    ) {
        return {
            payload: {
                payload,
                callback
            }
        }
    }
);

// Active Tasks Selectors
export const getTasksState = (state: RootState) => state.tasks;
export const getActiveTasks = createSelector(
    getTasksState,
    (tasks) => tasks.activeTasks
);
export const getSaveTaskDataLoading = createSelector(
    getActiveTasks,
    (activeTasks) => activeTasks.saveTaskDataLoading
);
export const getToggledExpandStatusIds = createSelector(
    getActiveTasks,
    (activeTasks) => activeTasks.toggledExpandStatusIds
);
export const getTasksActionFilterOptions = createSelector(
    getActiveTasks,
    (activeTasks) => activeTasks.actionFilterOptions
);
export const getSelectedTaskId = createSelector(
    getActiveTasks,
    (activeTasks) => activeTasks.activeData.selectedId
);
export const getCompanyIdByTaskSelectedId = createSelector(
    [getActiveTasks, getSelectedTaskId], 
    (activeTasks, selectedTaskId) => {
        const taskSelected = activeTasks.data.find((task) => task.Id === selectedTaskId);
        return taskSelected ? taskSelected.CompanyId : undefined;
    }
);
export const getTaskData = createSelector(
    getActiveTasks,
    (activeTasks) => activeTasks.activeData
);
export const getTaskDataInvoices = createSelector(
    getTaskData,
    (activeData) => activeData.invoices
);
export const getTasksData = createSelector(
    getActiveTasks,
    (activeTasks) => activeTasks.data
);
export const getTasksPageData = createSelector(
    getActiveTasks,
    (activeTasks) => activeTasks.pageData
);
export const getViewingUser = createSelector(
    getActiveTasks,
    (activeTasks) => activeTasks.viewingUser
);
export const getTaskWorkflows = createSelector(
    getTasksState,
    (tasks) => tasks.taskWorkflows
);


// Task History Selectors
export const getTaskHistoryState = (state: RootState) => state.tasks.taskHistory;
export const getTaskHistoryActiveData = createSelector(
    getTaskHistoryState,
    (taskHistory) => taskHistory.activeData
);
export const getTaskHistoryCancelledPaymentPlanTasks = createSelector(
    getTaskHistoryActiveData,
    (activeData) => activeData.cancelledPaymentPlanTasks
);
export const getSelectedTaskHistoryId = createSelector(
    getTaskHistoryActiveData,
    (activeData) => activeData.selectedId
);
export const getCompanyIdForSelectedTaskHistoryId = createSelector(
    getTaskHistoryActiveData,
    (activeData) => activeData.companyId
);
export const getTaskHistoryCollectionsTaskData = createSelector(
    getTaskHistoryActiveData,
    (activeData) => activeData.collections.actionedTasks.activeData
);
export const getTaskHistoryNotifications = createSelector(
    getTaskHistoryActiveData,
    (activeData) => activeData.notifications
);
export const getTaskHistoryNotificationData = createSelector(
    getTaskHistoryNotifications,
    (notificationStates) => notificationStates.activeData
);
export const getTaskHistoryNotificationDataTasks = createSelector(
    getTaskHistoryNotificationData,
    (notificationActiveData) => notificationActiveData.tasks
);
export const getTaskHistoryDetailsDeliveryData = createSelector(
    getTaskHistoryActiveData,
    (activeData) => activeData.detailsDelivery
);
export const getTaskHistoryChangedTasks = createSelector(
    getTaskHistoryActiveData,
    (activeData) => activeData.changedTasks
);
export const getTaskHistoryCollections = createSelector(
    getTaskHistoryActiveData,
    (activeData) => activeData.collections
);

// Reducer
export default reducer;
